import "./styles.css";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../common/utils/useProjectNavigate";
import axios from "axios";
import useSearch from "../administration/user-management/useSearch";
import useSubjects from "./useSubjects";
import InviteSubjectModal from "../../components/modals/invitations/subject/invite-subject";
import SubjectDashboard from "./dashboard/subject-dashboard";
import SubjectsList from "./subjects-list/subjects-list";
import Uploads from "./uploads/uploads";
import { Body } from "../../components/main-body/body";
import { useSelector } from "react-redux";
import { selectSettings } from "../../store/slices/settings";
import DropdownFilters from "../../components/dropdown-filters/dropdown-filters";
import ApiSubjectsDashboard from "../../api/subjects-dashboard";
import ApiSubjectsManagement from "../../api/subjects-manager";
import ApiSiteManagement from "../../api/site-managements";
import SubjectManagementAiButton from "./components/subject-management-ai/SubjectManagementAiButton";
import { selectUser, selectCurrentProject } from "../../store/slices/user";
import useNotification from "../../components/notification/notification";
import useAdminEvents from "../../hooks/useAdminEvents";
import { useCan } from "../../common/utils/usePermissions";

const SubjectManagement = () => {
  const { t } = useTranslation();
  const { hash, pathname } = useLocation();
  const navigate = useProjectNavigate();

  const currentUser = useSelector(selectUser);
  const currentProject = useSelector(selectCurrentProject);
  const settings = useSelector(selectSettings);
  const canInvite = useCan("subjects.invite");
  const canDelete = useCan("subjects.delete");
  const { openNotification } = useNotification();

  const sitesEnabled =
    settings?.find((s) => s.key === "sitesEnable")?.value === true;

  const currentView = pathname.split("/").pop() || "subjects";

  const [filters, setFilters] = useState({});
  const [cohortsList, setCohortsList] = useState([]);
  const [subjectList, setSubjectList] = useState([]);
  const [sites, setSites] = useState([]);
  const [sitesList, setSitesList] = useState([]);
  const [isOpen, setOpen] = useState(false);

  const { search, handleUpdateSearchValue } = useSearch();

  const routeView = hash ? hash.replace("#", "") : currentView;

  const routeStatus = useMemo(() => {
    switch (routeView) {
      case "pending":
        return "pending";
      case "disabled":
        return "disabled";
      case "subject-management":
      case "subjects":
        return "active";
      default:
        return null;
    }
  }, [routeView]);

  const effectiveFilters = useMemo(() => {
    const next = { ...(filters || {}) };

    if (routeView === "pending" || routeView === "disabled") {
      next.status = routeStatus;
    }

    return next;
  }, [filters, routeStatus, routeView]);

  const {
    subjects,
    hasMore,
    isLoading,
    fetchNext,
    resetAndFetch,
    upsertSubject,
    removeSubjects,
  } = useSubjects(search, effectiveFilters);

  const handleToogleModal = () => {
    if (isOpen) {
      resetAndFetch();
    }
    setOpen(!isOpen);
  };

  const getSites = async (deselect, query = "") => {
    return ApiSiteManagement.getSitesSimple({
      query,
    }).then(({ data }) => {
      const { sites } = data || {};
      setSites(sites || []);
      setSitesList((sites || []).map((c) => ({ id: c.id, name: c.name })));
    });
  };

  const getDashboardFilterItems = () => {
    ApiSubjectsDashboard.filterField().then((r) => {
      setCohortsList(
        r.data.cohorts?.map((c) => ({ id: c.id, name: c.value })) || []
      );
    });
  };

  const fetchSubjectList = async () => {
    ApiSubjectsManagement.getSubjectsSimple().then((r) => {
      setSubjectList(
        (r.data.subjects || []).map((s) => ({
          id: s.id,
          name: s.displayId,
        }))
      );
    });
  };

  const fetchEnrollInviteUserTemplate = () =>
    axios.get("api/v1/enrollment-fields/user");

  useAdminEvents({
    onCohortChanged: getDashboardFilterItems,
    onSubjectChanged: (payload) => {
      if (payload?.action === "updated" && payload?.subject) {
        upsertSubject(payload.subject);
        setSubjectList((prev) =>
          (prev || []).map((item) =>
            item.id === payload.subject.id
              ? { ...item, name: payload.subject.displayId || item.name }
              : item
          )
        );
        return;
      }

      if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
        const deletedIds = payload?.deletedIds || [payload?.id].filter(Boolean);
        removeSubjects(deletedIds);
        setSubjectList((prev) =>
          (prev || []).filter((item) => !deletedIds.includes(item.id))
        );
        return;
      }

      fetchSubjectList();
      resetAndFetch();
    },
  });

  useEffect(() => {
    fetchSubjectList();
    getDashboardFilterItems();

    if (sitesEnabled) {
      getSites();
    }
  }, []);

  const handleBulkDeleteSubjects = useCallback(async (ids, { onDone } = {}) => {
    try {
      const { data } = await ApiSubjectsManagement.deleteSubjects(ids);

      const failed = Array.isArray(data?.failed) ? data.failed : [];
      const failedIds = failed.map((item) => item.id);
      const deletedCount = Array.isArray(data?.deletedIds)
        ? data.deletedIds.length
        : ids.length - failedIds.length;

      if (deletedCount > 0) {
        openNotification?.({
          type: failedIds.length ? "warning" : "success",
          message: (
            <p>
              {deletedCount} {deletedCount > 1 ? "subjects were" : "subject was"} deleted
              {failedIds.length ? `, ${failedIds.length} failed` : ""}
            </p>
          ),
        });
      } else {
        openNotification?.({
          type: "error",
          message: <p>Failed to delete subjects</p>,
        });
      }

      onDone?.({ failedIds });

      await resetAndFetch();
    } catch (e) {
      console.error(e);

      openNotification?.({
        type: "error",
        message: <p>Failed to delete subjects</p>,
      });

      onDone?.({ failedIds: ids });
    }
  }, [openNotification, resetAndFetch]);

  useEffect(() => {
    if (sitesEnabled) {
      getSites();
    }
  }, [settings, sitesEnabled]);

  const headerTitle = useMemo(() => {
    switch (routeView) {
      case "uploads":
        return "Uploads";
      case "pending":
        return "Pending subjects";
      case "disabled":
        return "Blocked subjects";
      case "dashboard":
        return "Dashboard";
      case "subject-management":
      case "subjects":
      default:
        return "Subjects";
    }
  }, [routeView]);

  const headerProps = useMemo(
    () => ({
      title: headerTitle,
      search: { value: search, onHandleChange: handleUpdateSearchValue, testID: "subjects-search" },
      create: canInvite ? {
        label: t("subject_management.btn.add"),
        testID: "subjects-add",
        onClick: handleToogleModal,
        renderModalComponent: (
          <InviteSubjectModal
            isOpen={isOpen}
            close={handleToogleModal}
            fetchEnrollInviteUserTemplate={fetchEnrollInviteUserTemplate}
            sites={sites.map((x) => ({ id: x.id, label: x.name, value: x.id }))}
          />
        ),
      } : undefined,
    }),
    [headerTitle, search, isOpen, t, sites, canInvite]
  );

  const header = useMemo(() => {
    switch (routeView) {
      case "dashboard":
        return {};
      default:
        return headerProps;
    }
  }, [routeView, headerProps]);

  const openSubject = (subject) => {
    navigate(`/subject-management/subject/${subject.id}`);
  };

  const content = useMemo(() => {
    switch (routeView) {
      case "dashboard":
        return <SubjectDashboard />;
      default:
        return (
          <SubjectsList
            subjects={subjects}
            hasMore={hasMore}
            isLoading={isLoading}
            fetchUsers={fetchNext}
            onHandleRowClick={openSubject}
            onBulkDelete={canDelete ? handleBulkDeleteSubjects : undefined}
          />
        );
    }
  }, [routeView, subjects, hasMore, isLoading, fetchNext, handleBulkDeleteSubjects, canDelete]);

  const updateFilterValue = (value) => {
    setFilters(value || {});
  };

  const statuses = [
    { id: "active", name: "Active" },
    { id: "pending", name: "Pending" },
    { id: "disabled", name: "Disabled" },
  ];

  const headerFilter = useMemo(
    () => (
      <DropdownFilters
        filtersBy={[
          { name: "Subject", id: "subjectIds", children: subjectList },
          { name: "Cohort", id: "cohortIds", children: cohortsList },
          { name: "Status", id: "status", children: statuses },
          ...(sitesEnabled
            ? [{ name: "Sites", id: "siteIds", children: sitesList }]
            : []),
        ]}
        updateFilterValue={updateFilterValue}
        filter={filters}
      />
    ),
    [subjectList, cohortsList, sitesList, sitesEnabled, filters]
  );

  const subjectContext = useMemo(
    () => ({
      currentRoute: pathname,
      projectId: currentProject?.id,
      activeFilters: effectiveFilters,
      visibleDataSummary: {
        totalSubjects: subjects.length,
        activeCount: subjects.filter((s) => s.status === "confirmed").length,
        pendingCount: subjects.filter((s) =>
          ["invited", "inviteError", "created"].includes(s.status)
        ).length,
        disabledCount: subjects.filter((s) =>
          ["blocked", "inactive"].includes(s.status)
        ).length,
      },
    }),
    [pathname, currentProject, effectiveFilters, subjects]
  );

  return (
    <>
      {routeView === "uploads" ? (
        <Uploads />
      ) : (
        <Body filters={headerFilter} header={header}>
          {content}
        </Body>
      )}

      <SubjectManagementAiButton
        userId={currentUser?.id}
        subjectContext={subjectContext}
      />
    </>
  );
};

export default SubjectManagement;
